import React, { useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as MatchManagamentApi from "../../services/match.managament.service";
import * as clubAdmin from "../../services/clubAdmin.service";
import Swal from "sweetalert2";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { formateDateWithMonth, handleServerValidations, hasP } from "../../../../utils/commonfunction";
import { CustomProvider, DateRangePicker, Placeholder, Tooltip, Whisper } from "rsuite";
import NoRowsOverlay from "../../common/NoRowsOverlay";
import Breadcrums from "../../common/breadcrumbs";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const MatchManagamentList = () => {

  const params = useParams();
  const [searchParams] = useSearchParams();
  const isDev = searchParams.get("dev") === "true";
  const { t } = useTranslation();
  const navigate = useNavigate();
  const gridRef = useRef();
  const [searchData, setSearchData] = useState("");
  const [statsupdate, setStatusUpdate] = useState("false");
  const [pageIndex] = useState(Number(params.pgno) - 1 || 0);
  const [perPage, setPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("all");
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("Match Management"), url: isDev ? "/admin/match-management/match/list/1" : "" },
    ...(isDev ? [{ title: t("Create Match"), url: "/admin/match-management/match/add?dev=true" }] : []),
  ];

  const onSelectionChanged = (event) => {
    if (event && event.api) {
      const selectedNodes = event.api.getSelectedNodes();
      const selectedData = selectedNodes.map((node) => node.data);
      setSelectedRows(selectedData);
    }
  };

  const handleDeleteMultiple = () => {
    if (selectedRows.length === 0) return;
    const count = selectedRows.length;
    Swal.fire({
      customClass: "swal-wide",
      title: `Delete ${count} Selected Match${count > 1 ? "es" : ""}?`,
      html: `
        <div style="text-align: left; font-size: 14px; color: #4b5563; line-height: 1.5; margin-top: 8px;">
          <div style="background-color: #fef2f2; border: 1px solid #fca5a5; color: #991b1b; padding: 12px; border-radius: 6px; margin-bottom: 12px; display: flex; align-items: flex-start; gap: 8px;">
            <span style="font-size: 18px; line-height: 1;">⚠️</span>
            <div>
              <strong style="display: block; margin-bottom: 2px;">Warning: Irreversible Action</strong>
              Are you sure you want to delete <strong>${count} selected match(es)</strong>? All associated events, quarters, and report data will be permanently removed.
            </div>
          </div>
          <p style="margin-bottom: 6px; font-weight: 500; color: #1f2937;">
            Please type <span style="color: #dc2626; font-family: monospace; font-size: 15px; font-weight: 700;">DELETE</span> to confirm:
          </p>
        </div>
      `,
      input: "text",
      inputPlaceholder: 'Type "DELETE" to confirm',
      inputAttributes: {
        autocapitalize: "characters",
        autocomplete: "off",
        style: "text-align: center; font-weight: bold; letter-spacing: 2px; font-family: monospace; font-size: 15px; border: 1.5px solid #dc2626;",
      },
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: `Delete ${count} Match${count > 1 ? "es" : ""}`,
      cancelButtonText: t("btn_cancel") || "Cancel",
      reverseButtons: true,
      inputValidator: (value) => {
        if (!value || value.trim() !== "DELETE") {
          return 'Please type "DELETE" exactly to confirm.';
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Deleting...",
          text: "Please wait while selected matches are being deleted",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const deletePromises = selectedRows.map((row) => MatchManagamentApi.Delete(row.originalId));
        Promise.all(deletePromises)
          .then(() => {
            setSelectedRows([]);
            if (gridRef.current && gridRef.current.api) {
              gridRef.current.api.deselectAll();
            }
            Swal.fire({
              icon: "success",
              title: "Deleted!",
              text: `${count} match(es) deleted successfully.`,
              ...SWAL_SETTINGS,
            });
            refetch();
          })
          .catch((error) => {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(error),
              ...SWAL_SETTINGS,
            });
          });
      }
    });
  };

  React.useEffect(() => {
    clubAdmin.List({ limit: 1000, filters: { isProfileVerified: "verified", isBlocked: false } }).then((res) => {
      if (res.success) {
        const activeClubs = (res.data.list || []).filter(
          (c) => !c.isDeleted && 
                 c.isDeleted !== "true" && 
                 !c.deletedAt && 
                 c.status !== "DELETED" && 
                 (c.isProfileVerified === "verified" || c.isProfileVerified === true || c.isProfileVerified === "true") && 
                 !c.isBlocked
        );
        setClubs(activeClubs);
      }
    });
  }, []);

  // ================= FETCH MATCH DATA =================
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["match-list", selectedClub],
    queryFn: () => MatchManagamentApi.List({
      limit: 1000,
      lastKey: null,
      filters: { clubAdminId: selectedClub },
      sorting: {}
    }),
    refetchOnWindowFocus: false,
    retry: false,
    onError: (error) => {
      Swal.fire({
        icon: "error",
        text: handleServerValidations(error),
        ...SWAL_SETTINGS,
      });
    },
  });

  // ================= FETCH TEAM DATA ================= ✅
  // const { data: teamData, isLoading: teamLoading, isFetching: teamFetching } = useQuery({
  //   queryKey: ["team-list"],
  //   queryFn: () => TeamApi.List({
  //     limit: 1000,
  //     lastKey: null,
  //     filters: {},
  //     sorting: {}
  //   }),
  //   refetchOnWindowFocus: false,
  //   retry: false,
  //   onError: (error) => {
  //     Swal.fire({
  //       icon: "error",
  //       text: handleServerValidations(error),
  //       ...SWAL_SETTINGS,
  //     });
  //   },
  // });

  // ================= TEAM MAP (name -> id) ================= ✅
  // const teamMap = useMemo(() => {
  //   if (!teamData?.data?.list) return {};
  //   const map = {};
  //   teamData.data.list.forEach((team) => {
  //     if (team?.name) {
  //       map[team.name.toLowerCase()] = team.id;
  //     }
  //   });
  //   return map;
  // }, [teamData]);

  const viewfunction = (row) => {
    navigate(`/admin/match-management/match/view/${row.originalId}`);
  };

  const deletefunction = (id) => {
    Swal.fire({
      title: t("msg_are_you_sure"),
      text: t("you_not_be_able_to_revert_this"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("btn_yes_delete"),
      cancelButtonText: t("btn_cancel"),
      reverseButtons: true,
      customClass: "swal-wide",
      confirmButtonColor: "#f1388b",
      cancelButtonColor: "#403fad",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await MatchManagamentApi.Delete(id);
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message || t("operation_success"),
              ...SWAL_SETTINGS,
            });
            refetch();
          }
        } catch (error) {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        }
      }
    });
  };

  const deleteAllfunction = () => {
    Swal.fire({
      title: "Delete All Matches?",
      html: `
        <div style="text-align: left; font-size: 14px; color: #4b5563; line-height: 1.5; margin-top: 8px;">
          <div style="background-color: #fef2f2; border: 1px solid #fca5a5; color: #991b1b; padding: 12px; border-radius: 6px; margin-bottom: 12px; display: flex; align-items: flex-start; gap: 8px;">
            <span style="font-size: 18px; line-height: 1;">⚠️</span>
            <div>
              <strong style="display: block; margin-bottom: 2px;">Critical Warning: Destructive Action</strong>
              This will permanently delete <strong>all matches</strong> and all associated reports, events, and metrics from the database. This action <strong>cannot be undone</strong>.
            </div>
          </div>
          <p style="margin-bottom: 6px; font-weight: 500; color: #1f2937;">
            Please type <span style="color: #dc2626; font-family: monospace; font-size: 15px; font-weight: 700;">DELETE</span> to confirm:
          </p>
        </div>
      `,
      input: "text",
      inputPlaceholder: 'Type "DELETE" to confirm',
      inputAttributes: {
        autocapitalize: "characters",
        autocomplete: "off",
        style: "text-align: center; font-weight: bold; letter-spacing: 2px; font-family: monospace; font-size: 15px; border: 1.5px solid #dc2626;",
      },
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "I understand the consequences, delete all data",
      cancelButtonText: t("btn_cancel") || "Cancel",
      reverseButtons: true,
      customClass: "swal-wide",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      inputValidator: (value) => {
        if (!value || value.trim() !== "DELETE") {
          return 'Please type "DELETE" exactly to confirm.';
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        
        let progressSwal = Swal.fire({
            title: 'Deleting Data...',
            text: 'Starting deletion...',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        const processBatch = async () => {
           try {
              const response = await MatchManagamentApi.DeleteAll();
              if (response.success) {
                 if (response.data && response.data.hasMore) {
                    Swal.update({ text: response.message });
                    processBatch(); // recursively call to process next batch
                 } else {
                    Swal.close();
                    Swal.fire({
                      icon: "success",
                      text: response.message || "All matches deleted successfully.",
                      ...SWAL_SETTINGS,
                    });
                    refetch();
                 }
              } else {
                 Swal.close();
                 Swal.fire({
                    icon: "error",
                    text: response.message || "Error during deletion.",
                    ...SWAL_SETTINGS,
                 });
              }
           } catch (error) {
              Swal.close();
              Swal.fire({
                icon: "error",
                text: handleServerValidations(error),
                ...SWAL_SETTINGS,
              });
           }
        };

        processBatch();
      }
    });
  };

  const exportRawEventsPDF = async (row) => {
    try {
      Swal.fire({
        title: t("Exporting PDF..."),
        text: t("Fetching raw event logs..."),
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await MatchManagamentApi.GetRawEvents(row.homeTeamId, row.originalId);
      const events = response?.data || [];

      if (!events || events.length === 0) {
        Swal.close();
        Swal.fire({
          icon: "info",
          text: "No raw events recorded for this match.",
          ...SWAL_SETTINGS,
        });
        return;
      }

      const doc = new jsPDF();

      // Header Branding
      doc.setFontSize(22);
      doc.setTextColor(32, 128, 120); // #208078
      doc.text("mNetrics", 14, 20);

      doc.setFontSize(14);
      doc.setTextColor(44, 42, 99); // #2c2a63
      doc.text("Match Raw Event Logs Report", 14, 30);

      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(`Generated: ${moment().format("MMMM Do YYYY, h:mm:ss a")}`, 14, 37);

      doc.setDrawColor(230);
      doc.line(14, 42, 196, 42);

      // Match Details Meta Section
      const matchTitle = row.name || "Match Report";
      const homeTeam = row.homeTeamName || "Home Team";
      const awayTeam = row.awayTeam ? row.awayTeam.replace(/_/g, " ") : "Away Team";
      const dateTime = row.date
        ? moment(`${row.date} ${row.time || ""}`).format("DD MMM YYYY, HH:mm")
        : "N/A";
      const status = row.matchStatus || "N/A";

      doc.setFontSize(10);
      doc.setTextColor(50);
      doc.setFont(undefined, 'bold');
      doc.text(`Match Title:`, 14, 50);
      doc.setFont(undefined, 'normal');
      doc.text(`${matchTitle}`, 42, 50);

      doc.setFont(undefined, 'bold');
      doc.text(`Teams:`, 14, 56);
      doc.setFont(undefined, 'normal');
      doc.text(`${homeTeam} (Home) vs ${awayTeam} (Away)`, 42, 56);

      doc.setFont(undefined, 'bold');
      doc.text(`Date & Time:`, 14, 62);
      doc.setFont(undefined, 'normal');
      doc.text(`${dateTime}`, 42, 62);

      doc.setFont(undefined, 'bold');
      doc.text(`Status:`, 120, 62);
      doc.setFont(undefined, 'normal');
      doc.text(`${status}`, 140, 62);

      doc.setFont(undefined, 'bold');
      doc.text(`Total Events:`, 120, 56);
      doc.setFont(undefined, 'normal');
      doc.text(`${events.length}`, 145, 56);

      // Table Data
      const SUB_ACTIONS = ["TECHNICAL_SUBSTITUTION", "SUBSTITUTION", "TECHNICAL-SUBSTITUTION", "CHANGE_PLAYER_POSITION", "CHANGE-PLAYER-POSITION"];
      const tableColumn = ["Q", "Time", "Player", "Team", "Position", "Action", "Pts"];
      const tableRows = events.map(e => {
        const act = (e.actionType || "").toUpperCase();
        const isSub = SUB_ACTIONS.includes(act);
        return [
          `Q${e.quarter || 1}`,
          e.createdAt ? moment(e.createdAt).format("HH:mm:ss") : "-",
          e.playerName || "-",
          e.teamName || "-",
          e.position || "-",
          (e.actionType || "").replace(/-/g, " "),
          isSub ? 0 : (e.points ?? 0)
        ];
      });

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 68,
        theme: 'striped',
        headStyles: { fillColor: [32, 128, 120], fontSize: 9, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 2.5 },
        alternateRowStyles: { fillColor: [248, 252, 251] },
        margin: { top: 20 },
        didDrawPage: (data) => {
          const str = `Page ${doc.internal.getNumberOfPages()}`;
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(str, data.settings.margin.left, doc.internal.pageSize.height - 10);
        }
      });

      const sanitizeTitle = matchTitle.replace(/[^a-zA-Z0-9]/g, "_");
      const fileName = `mNetrics_RawEvents_${sanitizeTitle}_${moment().format("YYYYMMDD")}.pdf`;
      doc.save(fileName);

      Swal.close();
    } catch (error) {
      console.error("Error exporting raw events PDF:", error);
      Swal.close();
      Swal.fire({
        icon: "error",
        text: handleServerValidations(error) || "Failed to export raw events PDF.",
        ...SWAL_SETTINGS,
      });
    }
  };

  const exportChainsPDF = async (row) => {
    try {
      Swal.fire({
        title: t("Exporting PDF..."),
        text: t("Fetching chain events..."),
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await MatchManagamentApi.GetRawEvents(row.homeTeamId, row.originalId);
      const allEvents = response?.data || [];
      const chainEvents = allEvents.filter(e => e.chainId && e.chainId !== "NONE" && String(e.chainId).trim() !== "");

      if (!chainEvents || chainEvents.length === 0) {
        Swal.close();
        Swal.fire({
          icon: "info",
          text: "No chain events recorded for this match.",
          ...SWAL_SETTINGS,
        });
        return;
      }

      const doc = new jsPDF();

      // Header Branding
      doc.setFontSize(22);
      doc.setTextColor(32, 128, 120); // #208078
      doc.text("mNetrics", 14, 20);

      doc.setFontSize(14);
      doc.setTextColor(44, 42, 99); // #2c2a63
      doc.text("Match Possession Chains Report (Chains Only)", 14, 30);

      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(`Generated: ${moment().format("MMMM Do YYYY, h:mm:ss a")}`, 14, 37);

      doc.setDrawColor(230);
      doc.line(14, 42, 196, 42);

      // Match Details Meta Section
      const matchTitle = row.name || "Match Report";
      const homeTeam = row.homeTeamName || "Home Team";
      const awayTeam = row.awayTeam ? row.awayTeam.replace(/_/g, " ") : "Away Team";
      const dateTime = row.date
        ? moment(`${row.date} ${row.time || ""}`).format("DD MMM YYYY, HH:mm")
        : "N/A";
      const status = row.matchStatus || "N/A";

      const uniqueChainIds = [...new Set(chainEvents.map(e => e.chainId))];

      doc.setFontSize(10);
      doc.setTextColor(50);
      doc.setFont(undefined, 'bold');
      doc.text(`Match Title:`, 14, 50);
      doc.setFont(undefined, 'normal');
      doc.text(`${matchTitle}`, 42, 50);

      doc.setFont(undefined, 'bold');
      doc.text(`Teams:`, 14, 56);
      doc.setFont(undefined, 'normal');
      doc.text(`${homeTeam} (Home) vs ${awayTeam} (Away)`, 42, 56);

      doc.setFont(undefined, 'bold');
      doc.text(`Date & Time:`, 14, 62);
      doc.setFont(undefined, 'normal');
      doc.text(`${dateTime}`, 42, 62);

      doc.setFont(undefined, 'bold');
      doc.text(`Status:`, 120, 62);
      doc.setFont(undefined, 'normal');
      doc.text(`${status}`, 140, 62);

      doc.setFont(undefined, 'bold');
      doc.text(`Total Chains:`, 120, 50);
      doc.setFont(undefined, 'normal');
      doc.text(`${uniqueChainIds.length}`, 145, 50);

      doc.setFont(undefined, 'bold');
      doc.text(`Chain Events:`, 120, 56);
      doc.setFont(undefined, 'normal');
      doc.text(`${chainEvents.length}`, 145, 56);

      // Table Data
      const SUB_ACTIONS = ["TECHNICAL_SUBSTITUTION", "SUBSTITUTION", "TECHNICAL-SUBSTITUTION", "CHANGE_PLAYER_POSITION", "CHANGE-PLAYER-POSITION"];
      const tableColumn = ["Chain ID", "Q", "Time", "Player", "Team", "Position", "Action", "Pts"];
      const tableRows = chainEvents.map(e => {
        const act = (e.actionType || "").toUpperCase();
        const isSub = SUB_ACTIONS.includes(act);
        return [
          e.chainId || "-",
          `Q${e.quarter || 1}`,
          e.createdAt ? moment(e.createdAt).format("HH:mm:ss") : "-",
          e.playerName || "-",
          e.teamName || "-",
          e.position || "-",
          (e.actionType || "").replace(/-/g, " "),
          isSub ? 0 : (e.points ?? 0)
        ];
      });

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 68,
        theme: 'striped',
        headStyles: { fillColor: [111, 66, 193], fontSize: 9, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 2.5 },
        alternateRowStyles: { fillColor: [248, 245, 252] },
        margin: { top: 20 },
        didDrawPage: (data) => {
          const str = `Page ${doc.internal.getNumberOfPages()}`;
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(str, data.settings.margin.left, doc.internal.pageSize.height - 10);
        }
      });

      const sanitizeTitle = matchTitle.replace(/[^a-zA-Z0-9]/g, "_");
      const fileName = `mNetrics_ChainsOnly_${sanitizeTitle}_${moment().format("YYYYMMDD")}.pdf`;
      doc.save(fileName);

      Swal.close();
    } catch (error) {
      console.error("Error exporting chains PDF:", error);
      Swal.close();
      Swal.fire({
        icon: "error",
        text: handleServerValidations(error) || "Failed to export chains PDF.",
        ...SWAL_SETTINGS,
      });
    }
  };

  // ================= COLUMNS =================
  const colDefs = useMemo(() => [
    {
      headerName: "",
      field: "checkbox",
      maxWidth: 50,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      sortable: false,
      filter: false,
      suppressMovable: true,
      lockPosition: true,
    },
    {
      headerName: t("list_heading_sno"),
      field: "srNo",
      maxWidth: 85,
      sortable: false,
      filter: false,
      suppressMovable: true,
      lockPosition: true,
      valueGetter: (params) => {
        if (!params.node || params.node.rowIndex === undefined) return "";
        return params.node.rowIndex + 1;
      },
      cellStyle: { borderRight: "1px solid #dee2e6", color: "#3c4858" },
    },
    {
      headerName: t("Title"),
      field: "name",
      flex: 1,
      minWidth: 150,
      sortable: true,
      filter: true,
      tooltipField: "name",
      cellRenderer: (params) => {
        return (
          <div style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {params.value || "N/A"}
          </div>
        );
      },
    },
    {
      headerName: t("Match ID"),
      field: "matchCode",
      flex: 1,
      minWidth: 150,
      maxWidth: 170,
      sortable: true,
      filter: true,
    },
    {
      headerName: t("home name"),
      field: "homeTeamName",
      flex: 1,
      minWidth: 150,
      sortable: true,
      filter: true,
      tooltipField: "name",
      cellRenderer: (params) => {
        const homeTeamId = params.data?.homeTeamId;
        return (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              cursor: homeTeamId ? "pointer" : "default",
            }}
            onClick={() => {
              if (homeTeamId) {
                navigate(`/admin/team-management/team/view/${homeTeamId}`);
              }
            }}
          >
            {params.value || "N/A"}
          </div>
        );
      },
    },
    {
      headerName: t("away name"),
      field: "awayTeam",
      flex: 1,
      minWidth: 150,
      sortable: true,
      filter: true,
      tooltipField: "name",
      cellRenderer: (params) => {
        return (
          <div style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {params.value || "N/A"}
          </div>
        );
      },
    },
    {
      headerName: t("date & time"),
      field: "dateTime",
      flex: 1,
      minWidth: 150,
      maxWidth: 150,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        const date = params.data?.date;
        const time = params.data?.time;
        if (!date && !time) return "N/A";
        const formatted = date
          ? moment(`${date} ${time || ""}`).format("DD MMM YYYY, HH:mm")
          : "N/A";
        return (
          <div style={{ whiteSpace: "nowrap" }}>
            {formatted}
          </div>
        );
      },
    },
    {
      headerName: t("match status"),
      field: "matchStatus",
      flex: 1,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        const status = params.value?.toLowerCase();
        let badgeClass = "badge badge-pill fw-bold text-uppercase";
        if (status === "live") {
          badgeClass += " badge-danger-light";
        } else if (status === "finished") {
          badgeClass += " badge-warning-light";
        } else {
          badgeClass += " badge-success-light";
        }
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            <span className={badgeClass}>
              {params.value || "N/A"}
            </span>
          </div>
        );
      },
      cellStyle: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
    },
    {
      headerName: t("list_heading_created_date"),
      field: "createdAt",
      filter: "agDateColumnFilter",
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      valueGetter: (params) =>
        params.data?.createdAt ? new Date(params.data.createdAt) : null,
      valueFormatter: (params) =>
        params.value ? formateDateWithMonth(params.value) : "N/A"
    },
    ...(hasP("match-view") ? [{
      headerName: t("list_heading_action"),
      minWidth: 190,
      maxWidth: 220,
      pinned: "right",
      sortable: false,
      filter: false,
      cellRenderer: (params) => {
        const row = params.data;
        return (
          <div className="btn-toolbar table-actions">
            <div className="btn-group btn-group-sm">
              {hasP("match-view") && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip style={{ fontWeight: 'bold' }}>View</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn view"
                    onClick={() => viewfunction(row)}
                  >
                    <i className="ri-eye-fill"></i>
                  </button>
                </Whisper>
              )}
              <Whisper
                placement="top"
                trigger="hover"
                speaker={<Tooltip style={{ fontWeight: 'bold' }}>Export Raw Events PDF</Tooltip>}
              >
                <button
                  type="button"
                  className="btn view ms-2"
                  style={{ backgroundColor: "#208078", color: "#fff", borderColor: "#208078" }}
                  onClick={() => exportRawEventsPDF(row)}
                >
                  <i className="ri-file-pdf-fill"></i>
                </button>
              </Whisper>
              <Whisper
                placement="top"
                trigger="hover"
                speaker={<Tooltip style={{ fontWeight: 'bold' }}>Export Chains Only PDF</Tooltip>}
              >
                <button
                  type="button"
                  className="btn view ms-2"
                  style={{ backgroundColor: "#6f42c1", color: "#fff", borderColor: "#6f42c1" }}
                  onClick={() => exportChainsPDF(row)}
                >
                  <i className="ri-links-fill"></i>
                </button>
              </Whisper>
              <Whisper
                placement="top"
                trigger="hover"
                speaker={<Tooltip style={{ fontWeight: 'bold' }}>Delete</Tooltip>}
              >
                <button
                  type="button"
                  className="btn delete ms-2"
                  onClick={() => deletefunction(row.originalId)}
                >
                  <i className="ri-delete-bin-fill"></i>
                </button>
              </Whisper>
            </div>
          </div>
        );
      },
    }] : []),
  ], [t, navigate]);

  // ================= ROW DATA =================
  const rowData = useMemo(() => {
    if (!data?.data?.list) return [];

    return data.data.list
      .map((row) => {
        const homeTeamNameLower = row?.hometeamId?.toLowerCase() || "";
        // const homeTeamId = teamMap[homeTeamNameLower] || null;
        const homeTeamId = homeTeamNameLower || null

        return {
          srNo: 0,
          originalId: row.id,
          matchCode: row?.matchCode || "",
          name: `${row?.title}`.trim(),
          homeTeamName: row?.homeTeamName || "N/A",
          homeTeamId: homeTeamId,
          awayTeam: row?.awayTeam?.replace(/_/g, " ") || "N/A",
          date: row?.date || "N/A",
          time: row?.time || "N/A",
          matchStatus: row?.matchStatus || "N/A",
          matchstatus: row?.matchstatus || "N/A",
          status: row.status,
          createdAt: row.createdAt,
        };
      })
      .filter((row) => {
        const matchesSearch = searchData
          ? row.name.toLowerCase().includes(searchData.toLowerCase()) ||
          row?.homeTeamName?.toLowerCase().includes(searchData?.toLowerCase()) ||
          row?.awayTeam?.toLowerCase().includes(searchData?.toLowerCase())
          : true;
        const matchesStatus =
          statusFilter === "all"
            ? true
            : row.matchStatus?.toLowerCase() === statusFilter;
        return matchesSearch && matchesStatus;
      });
  }, [data, searchData, statusFilter]);

  // ================= AG GRID CONFIG =================
  const getAgGridConfig = useMemo(() => ({
    ref: gridRef,
    domLayout: "autoHeight",
    rowData: rowData,
    columnDefs: colDefs,
    rowSelection: "multiple",
    onSelectionChanged: onSelectionChanged,
    getRowId: (params) => String(params.data.originalId),
    noRowsOverlayComponent: NoRowsOverlay,
    pagination: true,
    paginationPageSize: perPage,
    paginationPageSizeSelector: [10, 20, 50, 100],
    suppressCellFocus: true,
    defaultColDef: {
      resizable: true,
      sortable: true,
      minWidth: 150,
      cellStyle: {
        padding: "10px 8px",
      },
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    },
    headerHeight: 50,
    rowHeight: 45,
    onPaginationChanged: (params) => {
      if (params.api) {
        const pageSize = params.api.paginationGetPageSize();
        setPerPage(pageSize);
      }
    },
  }), [rowData, colDefs, perPage]);

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card overflow-hidden">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center gap-3">
                <h6 className="main-content-label mb-0">Match Management</h6>
                {selectedRows.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm d-flex align-items-center gap-1"
                    onClick={handleDeleteMultiple}
                  >
                    <i className="ri-delete-bin-line"></i> Delete Selected ({selectedRows.length})
                  </button>
                )}
              </div>
              <div className="d-flex align-items-center gap-2">

                {/* ===== SEARCH ===== */}
                <div style={{ width: 300 }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("input_placeholder_search")}
                    value={searchData}
                    onChange={(e) => setSearchData(e.target.value)}
                    style={{ outline: "none", boxShadow: "none" }}
                  />
                </div>

                {/* ===== STATUS FILTER DROPDOWN ===== */}
                <div className="me-3" style={{ position: "relative" }}>
                  <select
                    className="form-control"
                    style={{
                      width: "100%",
                      outline: "none",
                      boxShadow: "none",
                      appearance: "none",
                      WebkitAppearance: "none",
                      cursor: "pointer",
                    }}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">Status</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="live">Live</option>
                    <option value="finished">Finished</option>
                  </select>
                  <i
                    className="ri-arrow-down-s-line"
                    style={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                      fontSize: 18,
                      color: "#6c757d",
                    }}
                  />
                </div>

                {/* ===== CLUB FILTER DROPDOWN ===== */}
                <div className="me-3" style={{ position: "relative" }}>
                  <select
                    className="form-control"
                    style={{
                      width: "100%",
                      outline: "none",
                      boxShadow: "none",
                      appearance: "none",
                      WebkitAppearance: "none",
                      cursor: "pointer",
                    }}
                    value={selectedClub}
                    onChange={(e) => setSelectedClub(e.target.value)}
                  >
                    <option value="">{t("sidebar_link_club")}</option>
                    {clubs.map((club) => (
                      <option key={club.id} value={club.id}>
                        {club.clubDetails?.name || club.firstName}
                      </option>
                    ))}
                  </select>
                  <i
                    className="ri-arrow-down-s-line"
                    style={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                      fontSize: 18,
                      color: "#6c757d",
                    }}
                  />
                </div>

                {/* date */}
                <div>
                  <CustomProvider>
                    <DateRangePicker
                      style={{ width: "150px" }}
                      placeholder={t("input_placeholder_date")}
                      placement="bottomEnd"
                      onChange={(value) => {
                        if (!gridRef.current || !gridRef.current.api) return;

                        if (!value || value.length !== 2) {
                          gridRef.current.api.setFilterModel(null);
                          gridRef.current.api.onFilterChanged();
                          return;
                        }
                        const [start, end] = value;
                        gridRef.current.api.setFilterModel({
                          createdAt: {
                            type: "inRange",
                            dateFrom: moment(start).startOf("day").format("YYYY-MM-DD HH:mm:ss"),
                            dateTo: moment(end).endOf("day").format("YYYY-MM-DD HH:mm:ss")
                          }
                        });
                        gridRef.current.api.onFilterChanged();
                      }}
                    />
                  </CustomProvider>
                </div>
                {isDev && (
                  <button
                    className="btn btn-primary d-flex align-items-center gap-2"
                    style={{ background: "#008080", borderColor: "#008080", color: "white" }}
                    onClick={() => navigate("/admin/match-management/match/add?dev=true")}
                  >
                    <i className="fe fe-plus"></i> {t("btn_add")} {t("Match")}
                  </button>
                )} 
              </div>
            </div>

            {isLoading || isFetching ? ( // ✅ team loading bhi check
              <Placeholder.Paragraph
                className="w-100 admin-placeholder"
                rowHeight={30}
                rowSpacing={5}
                active={true}
                rows={7}
              />
            ) : (
              <div className="ag-theme-alpine" style={{ width: '100%' }}>
                <AgGridReact {...getAgGridConfig} />
              </div>
            )}
          </div>
        </div>

        {/* ===== DANGER / ADVANCED ACTIONS AREA ===== */}
        <div className="card custom-card overflow-hidden mt-4 border border-danger-subtle" style={{ borderColor: "#fed7d7" }}>
          <div className="card-header py-3 d-flex align-items-center justify-content-between" style={{ backgroundColor: "#fff5f5", borderBottom: "1px solid #fed7d7" }}>
            <div className="d-flex align-items-center gap-2">
              <i className="ri-error-warning-fill text-danger" style={{ fontSize: "1.2rem" }}></i>
              <h6 className="main-content-label mb-0 text-danger">Danger / Advanced Actions</h6>
            </div>
            <span className="badge badge-pill badge-danger-light text-danger fw-bold">Admin Only</span>
          </div>
          <div className="card-body">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
              <div>
                <h6 className="fw-semibold text-dark mb-1">Delete All Match Data</h6>
                <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                  Permanently remove all match records, timeline events, possession chains, and generated reports from the system. This action cannot be reversed.
                </p>
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn-danger d-flex align-items-center gap-2 text-nowrap px-3 py-2"
                  style={{ background: "#dc3545", borderColor: "#dc3545", color: "white", fontWeight: "600" }}
                  onClick={deleteAllfunction}
                >
                  <i className="ri-delete-bin-fill"></i> Delete All Data
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MatchManagamentList;